import React from 'react'
import PropTypes from 'prop-types'
import {sum, times, random, map, groupBy} from 'lodash-es'
import {Skeleton, Grid, GridItem, useTheme} from '@chakra-ui/react'
import {
  flexRender,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/react-table'
import {columnsPropType, sortByOptionsPropType} from './propTypes'

import {Header as TableHeader} from '../Table'

const TableStyles = (variant) => {
  switch (variant) {
    case 'cashIn':
      return {
        tr: {
          borderBottom: '1px solid',
          borderColor: 'grey.300',
        },
        th: {
          px: 0,
        },
        td: {
          px: '0',
          py: {base: '4', md: '7'},
          my: 'auto',
          textAlign: 'start',
          fontSize: 'base',
        },
      }
    case 'sm':
      return {
        tr: {
          borderBottom: '1px solid',
          borderColor: 'grey.300',
        },
        th: {
          px: 0,
        },
        td: {
          px: '0',
          py: {base: '4', md: '7'},
          my: 'auto',
          textAlign: 'start',
          fontSize: 'sm',
        },
      }
    default:
      return {
        tr: {
          borderBottom: '1px solid',
          borderColor: 'grey.300',
        },
        td: {
          px: '8',
          py: {base: '4', md: '7'},
          my: 'auto',
          textAlign: 'start',
          fontSize: 'base',
        },
      }
  }
}

const TableRow = ({data, onClickRow, variant}) => {
  const onClickRowHandler = React.useCallback(() => {
    // Use original data supplied instead of displayed data
    if (onClickRow) onClickRow(data.original)
  }, [data, onClickRow])
  const cells = data.getVisibleCells()
  const unwrappedCells = cells.filter((cell) => !cell.column.columnDef.wrap)
  const gridLength = sum(
    unwrappedCells.map((cell) => cell.column.columnDef.colSpan || 1),
  )

  return (
    <Grid
      role="row"
      templateColumns={`repeat(${gridLength}, minmax(0, 1fr))`}
      layout
      exit="collapsed"
      variants={{
        open: {opacity: 1, height: 'auto'},
        collapsed: {opacity: 0, height: 0},
      }}
      onClick={onClickRowHandler}
      {...TableStyles(variant).tr}
      _hover={
        Boolean(onClickRow) && {
          backgroundColor: 'neutral.smoke',
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'neutral.glitter',
          cursor: 'pointer',
        }
      }
    >
      {cells.map((cell) => {
        // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
        const {meta, colSpan} = cell.column.columnDef
        return (
          <GridItem
            role="cell"
            key={[data.original.id, cell.id].join('_')}
            isNumeric={meta?.isNumeric}
            colSpan={colSpan || 1}
            {...TableStyles(variant).td}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </GridItem>
        )
      })}
    </Grid>
  )
}

TableRow.defaultProps = {
  onClickRow: undefined,
  variant: 'default',
}

TableRow.propTypes = {
  variant: PropTypes.string,
  data: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
      PropTypes.number,
      PropTypes.array,
      PropTypes.func,
    ]),
  ).isRequired,
  onClickRow: PropTypes.func,
}

const ResponsiveDataTable = ({
  data,
  columns,
  onClickRow,
  isLoading,
  enableSorting,
  onSort,
  sortByValue,
  manualSorting,
  variant,
  groupByValue,
  className,
}) => {
  const [sorting, setSorting] = React.useState(sortByValue)

  // Memoize the table columns updating only when the columns themselves change.
  const tableColumns = React.useMemo(() => columns, [columns])
  const columnVisibility = {}
  tableColumns.forEach((column) => {
    const key = column.id ?? column.accessorKey
    columnVisibility[key] = !column.hidden
  })

  const {getHeaderGroups, getFooterGroups, getRowModel} = useReactTable({
    columns: tableColumns,
    data,
    manualSorting,
    enableSorting,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
      columnVisibility,
    },
  })

  React.useEffect(() => {
    onSort(sorting)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting])

  const {rows} = getRowModel()

  const groupedRows =
    rows && groupByValue
      ? groupBy(rows, (row) => row.getValue(groupByValue))
      : {}

  const headerGroups = getHeaderGroups()
  const footerGroups = getFooterGroups()

  return (
    <Grid role="table" {...TableStyles(variant).table} className={className}>
      <Grid role="rowgroup">
        {headerGroups.map((headerGroup) => {
          const headers = headerGroup.headers.filter(
            (header) => !header.column.columnDef.wrap,
          )
          const gridLength = sum(
            headers.map((header) => header.column.columnDef.colSpan || 1),
          )

          return (
            <Grid
              key={headerGroup.id}
              role="row"
              templateColumns={`repeat(${gridLength}, minmax(0, 1fr))`}
              borderTop="1px solid"
              borderBottom="1px solid"
              borderColor="mld.neutral.800"
              _dark={{
                borderColor: 'mld.neutral.100',
              }}
            >
              {headers.map((header) => {
                const {column} = header
                // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                const {meta, colSpan} = column.columnDef
                return (
                  <GridItem
                    key={header.id}
                    colSpan={colSpan || 1}
                    onClick={column.getToggleSortingHandler()}
                    isNumeric={meta?.isNumeric}
                  >
                    <TableHeader
                      canSort={column.getCanSort()}
                      isSorted={column.getIsSorted()}
                      showSortIcon
                      {...TableStyles(variant).th}
                    >
                      {flexRender(column.columnDef.header, header.getContext())}
                    </TableHeader>
                  </GridItem>
                )
              })}
            </Grid>
          )
        })}
      </Grid>

      {isLoading && <LoadingSkeleton columns={columns} variant={variant} />}

      {rows.length > 0 && (
        <Grid role="rowgroup">
          {Object.keys(groupedRows).length > 0 && (
            <>
              {map(groupedRows, (groupRows, groupValue) => (
                <>
                  <Grid
                    role="row"
                    backgroundColor="grey.100"
                    {...TableStyles(variant).tr}
                  >
                    <GridItem
                      role="cell"
                      colSpan={sum(
                        headerGroups.map(
                          (headerGroup) => headerGroup.headers.length,
                        ),
                      )}
                      {...TableStyles(variant).td}
                      py="xs"
                      ml="xs"
                      fontSize="sm"
                      fontWeight="medium"
                      color="neutral.800"
                    >
                      {groupValue}
                    </GridItem>
                  </Grid>
                  {groupRows.map((row) => (
                    <TableRow
                      key={row.original.id || row.id}
                      variant={variant}
                      data={row}
                      onClickRow={onClickRow}
                    />
                  ))}
                </>
              ))}
            </>
          )}

          {Object.keys(groupedRows).length === 0 && (
            <>
              {rows.map((row) => (
                <TableRow
                  key={row.original.id || row.id}
                  variant={variant}
                  data={row}
                  onClickRow={onClickRow}
                />
              ))}
            </>
          )}
        </Grid>
      )}

      <Grid role="rowgroup">
        {footerGroups.map(
          (footerGroup) =>
            !!footerGroup.headers.filter(
              (header) => header.column.columnDef.footer,
            ).length && (
              <Grid
                key={footerGroup.id}
                role="row"
                templateColumns={`repeat(${footerGroup.headers.length}, 1fr)`}
                borderBottom="1px solid"
                {...TableStyles(variant).tr}
              >
                {footerGroup.headers.map((header) => (
                  <GridItem
                    key={header.id}
                    role="columnheader"
                    colSpan={header.colSpan}
                    {...TableStyles(variant).td}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.footer,
                          header.getContext(),
                        )}
                  </GridItem>
                ))}
              </Grid>
            ),
        )}
      </Grid>
    </Grid>
  )
}

ResponsiveDataTable.defaultProps = {
  data: [],
  onClickRow: undefined,
  onSort: () => {},
  isLoading: false,
  sortByValue: [],
  manualSorting: false,
  enableSorting: false,
  chakraTableProps: {},
  groupByValue: undefined,
  variant: 'default',
  className: '',
}

ResponsiveDataTable.propTypes = {
  variant: PropTypes.string,
  chakraTableProps: PropTypes.shape({
    size: PropTypes.string,
    variant: PropTypes.string,
  }),
  columns: columnsPropType.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
      PropTypes.object,
      PropTypes.bool,
      PropTypes.func,
    ]),
  ),
  onClickRow: PropTypes.func,
  isLoading: PropTypes.bool,
  enableSorting: PropTypes.bool,
  onSort: PropTypes.func,
  manualSorting: PropTypes.bool,
  sortByValue: sortByOptionsPropType,
  groupByValue: PropTypes.string,
  className: PropTypes.string,
}

export const LoadingSkeleton = ({rows, columns, variant}) => {
  const theme = useTheme()

  const visibleColumns = columns.filter((column) => !column.hidden)
  const unwrappedColumns = visibleColumns.filter((column) => !column.wrap)
  const gridLength = sum(unwrappedColumns.map((column) => column.colSpan || 1))

  return (
    <Grid role="rowgroup">
      {times(rows, (row) => (
        <Grid
          role="row"
          key={row}
          templateColumns={`repeat(${gridLength}, 1fr)`}
          {...TableStyles(variant).tr}
        >
          {visibleColumns.map((column) => (
            <GridItem
              role="cell"
              key={`${column.accessorKey}-${row}`}
              colSpan={column.colSpan || 1}
              {...TableStyles(variant).td}
            >
              <Skeleton
                borderRadius="md"
                height="24px"
                width={random(0.5, 0.8)}
                startColor={theme.colors.skeleton.light}
                endColor={theme.colors.skeleton.dark}
              />
            </GridItem>
          ))}
        </Grid>
      ))}
    </Grid>
  )
}

LoadingSkeleton.defaultProps = {
  rows: 10,
  variant: 'default',
}

LoadingSkeleton.propTypes = {
  columns: columnsPropType.isRequired,
  rows: PropTypes.number,
  variant: PropTypes.string,
}

export default ResponsiveDataTable
